import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import Reveal, { Stagger, StaggerItem } from "@/components/motion/Reveal";

const DISTRICTS = [
  "Bengaluru Urban",
  "Bengaluru Rural",
  "Mysuru",
  "Tumakuru",
  "Kolar",
  "Chikkaballapur",
  "Ramanagara",
  "Mandya",
  "Hassan",
  "Chitradurga",
  "Ballari",
  "Belagavi",
  "Dharwad",
  "Hubballi",
  "Shivamogga",
  "Udupi",
];

export default function Karnataka() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Parallax background + horizontal drift on the mega title
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1.3]);
  const titleX = useTransform(scrollYProgress, [0, 1], ["4%", "-8%"]);

  return (
    <section
      id="karnataka"
      ref={ref}
      data-testid="karnataka-section"
      className="relative bg-[#08080A] py-32 md:py-48 overflow-hidden"
    >
      {/* Parallax background image */}
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="https://images.unsplash.com/photo-1606141836992-bfcb00c776c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxrYXJuYXRha2ElMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzNDMzNTY2fDA&ixlib=rb-4.1.0&q=85"
          alt="Karnataka land"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-[#08080A]/70 to-[#08080A]" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal variant="fade" className="mb-14">
          <div className="overline mb-6 flex items-center gap-3">
            <MapPin strokeWidth={1} className="w-4 h-4" />
            Area of Operation
          </div>
        </Reveal>

        {/* Massive typographic overlay with clip reveal + drift */}
        <motion.div className="relative" style={{ x: titleX }}>
          <Reveal variant="clip" duration={1.1}>
            <h2
              data-testid="karnataka-mega-title"
              className="font-display text-[22vw] md:text-[16vw] lg:text-[13vw] leading-[0.85] tracking-tighter text-neutral-50 uppercase select-none"
            >
              Karnataka.
            </h2>
          </Reveal>
          <div className="absolute right-0 -bottom-2 md:bottom-6 font-display italic text-2xl md:text-4xl text-[#D4AF37]">
            Every acre, known.
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-16">
          <Reveal variant="up" className="md:col-span-5 md:col-start-1">
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed font-display">
              We do one state. We do it deeply.
            </p>
            <p className="mt-6 text-neutral-400 leading-relaxed">
              RUDRA EMPIRE has confined its operations to Karnataka by design.
              Three decades of relationships — with pattadars, revenue offices,
              village accountants and district administrations — allow us to
              move from first conversation to registered deed with a quiet
              certainty that state-agnostic firms cannot replicate.
            </p>
          </Reveal>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal variant="fade" className="overline mb-6">
              Active Districts
            </Reveal>
            <Stagger className="flex flex-wrap gap-2" gap={0.04}>
              {DISTRICTS.map((d) => (
                <StaggerItem key={d}>
                  <span className="inline-block px-4 py-2 border border-white/10 text-xs tracking-widest uppercase text-neutral-300 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 cursor-default">
                    {d}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}
